import { SHIP_MODE_CODE } from '@/data/constants/order';
import {
	FILTERED_PROMO_ICONS,
	PRODUCT_CARD_PROMO_ICONS_VALIDATE_STORE_EXCLUDE,
	PROMOTION_TYPES,
} from '@/data/constants/promoIconConstants';
import { dFix } from '@/data/Settings';
import { OrderItem } from '@/data/types/Order';
import { ProductType, ResponseProductAttribute } from '@/data/types/Product';
import { getStoreDataByPhysicalStoreId } from '@/data/utils/commonIntegrationUtils';
import { validateStoreExclude } from '@/data/utils/validateStoreExclude';
import {
	FACEBOOK_DELIVERY_CATEGORIES,
	PINTEREST_DELIVERY_CATEGORIES,
} from 'presentation/events/constants/eventConstants';
import { PromotionDetails } from 'presentation/events/hooks/useGetPromotionDetails';
import { ProductWithCurrentOrderDataType } from 'presentation/events/types/checkoutEventsTypes';
import {
	IS_PARTNUMBER_FROM_BUNDLE_KEY,
	MARKET_BUYABLE_EXCLUDE,
	PRODUCT_PRICE_USAGE,
	STORE_BUYABLE_EXCLUDE,
	STORE_ONLY_AVAILABLE,
} from 'shared/custom/constants/commonConstants';

export const getProductQuantity = ({
	quantity,
}: {
	quantity: string | number | undefined;
}): number => {
	if (quantity === undefined) {
		return 1;
	}

	if (typeof quantity === 'string') {
		return parseFloat(quantity);
	}

	return quantity;
};

export const getProductPrice = ({
	product,
	promotionDetails,
	...props
}:
	| {
			product: ProductWithCurrentOrderDataType;
			quantity: number | string;
			promotions: string[];
			promotionDetails?: PromotionDetails;

			physicalStoreId?: never;
	  }
	| {
			product: ProductWithCurrentOrderDataType;
			quantity: number | string;
			physicalStoreId: string;
			promotionDetails?: PromotionDetails;

			promotions?: never;
	  }) => {
	let promotions: string[] = [];

	if (props.promotions) {
		promotions = props.promotions;
	}

	if (props.physicalStoreId) {
		promotions = getProductPromotions({
			productAttributes: product.attributes ?? [],
			physicalStoreId: props.physicalStoreId,
		});
	}

	const offerPrice = getProductOfferPrice({ product });

	if (!offerPrice) {
		return {
			price: 0,
			discount: 0,
		};
	}

	const quantity = getProductQuantity({ quantity: props.quantity });
	let price = offerPrice.value;
	let discount = 0;

	/**
	 * Remate Discount
	 */
	const RemateDiscount = getRematePromotionDiscount({
		product,
		promotions,
		offerPrice,
	});

	/**
	 * NLP Discount
	 */
	const NLPDiscount = getNLPPromotionDiscount({
		product,
		promotions,
		offerPrice,
	});

	discount += NLPDiscount + RemateDiscount;

	/**
	 * C Discount
	 */
	const CDiscount = getCPromotionDiscount({
		product,
		promotions,
		offerPrice,
	});

	const CDiscountForCheckout = getCPromotionDiscountForCheckout({
		product,
		quantity,
	});

	// Here the CDiscount is only for Combo SKU's that have the Combo promotion applied directly on them.
	// The ComboDiscountForCheckout is for the SKU's that are part of a Combo, but don't have the Combo promotion applied directly on them.
	discount += CDiscount;

	/**
	 * NXM Discount and PM Discount
	 */
	const NXMDiscount = getNXMPromotionDiscount({
		price,
		promotions,
		quantity,
		promotionDetails,
	});

	const PMDiscount = getPMPromotionDiscount({
		price,
		promotions,
		quantity,
		promotionDetails,
	});

	const PGDiscount = getPGPromotionDiscount({
		product,
		quantity,
		price,
	});

	// Apply best discount
	const maxDiscount = Math.max(NXMDiscount, PMDiscount, PGDiscount, CDiscountForCheckout);
	price -= maxDiscount;
	discount += maxDiscount;

	// Round price to 3 decimals
	price = dFix(price, 3);

	// Round discount to 3 decimals
	discount = dFix(discount, 3);

	return {
		price,
		discount,
	};
};

/**
 * Get NLP promotion discount
 */
const getNLPPromotionDiscount = ({
	product,
	promotions,
	offerPrice,
}: {
	product: ProductType;
	promotions: string[];
	offerPrice: ProductOfferPrice;
}) => {
	const NLPPromotion = promotions.find((promo) => promo === PROMOTION_TYPES.PROMOICON_NLP);
	if (!NLPPromotion) return 0;

	const displayPrice = getProductDisplayPrice({ product });
	if (!displayPrice) return 0;

	return Math.max(displayPrice.value - offerPrice.value, 0);
};
/*
 * Get Remate promotion discount
 */
const getRematePromotionDiscount = ({
	product,
	promotions,
	offerPrice,
}: {
	product: ProductType;
	promotions: string[];
	offerPrice: ProductOfferPrice;
}) => {
	const RematePromotion = promotions.find((promo) => promo === PROMOTION_TYPES.PROMOICON_GR);
	if (!RematePromotion) return 0;

	const displayPrice = getProductDisplayPrice({ product });
	if (!displayPrice) return 0;

	return Math.max(displayPrice.value - offerPrice.value, 0);
};

/**
 * Get C (Combo) promotion discount
 */
export const getCPromotionDiscount = ({
	product,
	promotions,
	offerPrice,
}: {
	product: ProductWithCurrentOrderDataType;
	promotions: string[];
	offerPrice: ProductOfferPrice;
}) => {
	const CPromotion = promotions.find((promo) => promo === PROMOTION_TYPES.PROMORIBBON_COMBO);
	if (!CPromotion) return 0;

	const components = product.components ?? [];

	if (!components.length) return 0;

	const componentsDisplayPrice = components.reduce((acc, component) => {
		const componentDisplayPrice = getProductDisplayPrice({
			product: component,
		});

		const componentQuantity = getProductQuantity({
			quantity: component.quantity,
		});

		if (!componentDisplayPrice) return acc;

		if (componentDisplayPrice.field1.LP > 0) {
			return acc + componentDisplayPrice.field1.LP * componentQuantity;
		}

		return acc + componentDisplayPrice.value * componentQuantity;
	}, 0);

	return Math.max(componentsDisplayPrice - offerPrice.value, 0);
};

/**
 * Get NXM promotion discount
 */
const getNXMPromotionDiscount = ({
	price,
	promotions,
	quantity,
	promotionDetails,
}: {
	price: number;
	promotions: string[];
	quantity: number;
	promotionDetails: PromotionDetails | undefined;
}) => {
	const NXMPromotion = promotions.find((promo) =>
		promo.startsWith(PROMOTION_TYPES.PROMORIBBON_NXM_)
	);
	if (!NXMPromotion) return 0;

	const promotionData = promotionDetails?.promotionData;
	if (!promotionData) return 0;

	const baseQty = promotionData.baseQty;
	const giftQty = promotionData.giftQty;

	if (baseQty === undefined || giftQty === undefined) return 0;

	// TODO: Test this logic!
	// See PR for reference: http://10.21.70.17/THDMX/ITPortfolio/_git/ecomm_StoreNextB2C/pullrequest/68299

	const promotionsToApply = Math.floor(quantity / baseQty);
	if (promotionsToApply < 1) return 0;

	const totalFreeProducts = giftQty * promotionsToApply;

	const totalDiscount = price * totalFreeProducts;
	const discountPerProduct = totalDiscount / quantity;

	return discountPerProduct;
};

const getPGPromotionDiscount = ({
	product,
	quantity,
	price,
}: {
	product: ProductWithCurrentOrderDataType;
	quantity: number;
	price: number;
}) => {
	if (!product.isGift) return 0;

	const adjustments = product.adjustments ?? [];
	const freeGiftPromoCodes = product.freeGiftPromotionCodes ?? [];

	if (product.freeQty) {
		const freeQtyNumber = parseFloat(product.freeQty);
		if (!Number.isNaN(freeQtyNumber) && freeQtyNumber > 0) {
			const priceReduction = freeQtyNumber * price;
			return priceReduction / quantity;
		}
	}

	if (adjustments.length === 0) return 0;
	if (freeGiftPromoCodes.length === 0) return 0;

	const hasPGPromotion = adjustments.some((adj) =>
		adj.code ? freeGiftPromoCodes.includes(adj.code) : false
	);

	if (!hasPGPromotion) return 0;

	const totalAdjustmentForAllProducts = adjustments.reduce((acc, adj) => {
		if (!adj.code) return acc;
		if (!freeGiftPromoCodes.includes(adj.code)) return acc;

		const adjValue = Math.abs(parseFloat(adj.amount));
		if (Number.isNaN(adjValue)) return acc;

		return acc + adjValue;
	}, 0);

	const discountPerProduct = totalAdjustmentForAllProducts / quantity;

	return discountPerProduct;
};

/**
 * Get PM (Precio Mayoreo) promotion discount
 */
export const getPMPromotionDiscount = ({
	price,
	promotions,
	quantity,
	promotionDetails,
}: {
	price: number;
	promotions: string[];
	quantity: number;
	promotionDetails: PromotionDetails | undefined;
}) => {
	const PMPromotion = promotions.find((promo) => promo === PROMOTION_TYPES.PROMORIBBON_AHP);
	if (!PMPromotion) return 0;

	const savingsPackData = promotionDetails?.savingsPackData;
	if (!savingsPackData) return 0;

	const longDesc = savingsPackData.longDesc;
	if (!longDesc) return 0;

	const [promotionQuantityStr, promotionPercentageStr] = longDesc.split('||');
	if (!promotionQuantityStr || !promotionPercentageStr) return 0;

	const promotionQuantity = parseFloat(promotionQuantityStr);
	const promotionPercentage = parseFloat(promotionPercentageStr);

	if (Number.isNaN(promotionQuantity) || Number.isNaN(promotionPercentage)) return 0;

	if (quantity < promotionQuantity) return 0;

	// TODO: Test this logic!

	return (price * promotionPercentage) / 100;
};

/**
 * Get C (combo) promotion discount for checkout events
 *
 * - view_cart
 * - begin_checkout
 * - add_shipping_info
 * - add_payment_info
 * - purchase
 *
 * For these events, we only have available the individual SKU's
 * that apply to the Combo, not the Combo partnumber itself.
 * So we need to check the adjustments of the SKU's for the
 *  Combo promotion code, and if it exists, apply the discount to those SKU's.
 */
export const getCPromotionDiscountForCheckout = ({
	product,
	quantity,
}: {
	product: ProductWithCurrentOrderDataType;
	quantity: number;
}) => {
	if (!product.isPartOfCombo || !product.comboDiscountAmount) return 0;

	const discount =
		typeof product.comboDiscountAmount === 'string'
			? parseFloat(product.comboDiscountAmount)
			: product.comboDiscountAmount;

	if (Number.isNaN(discount)) {
		return 0;
	}

	return Math.abs(discount / quantity);
};

export const getProductPromotions = ({
	productAttributes,
	physicalStoreId,
}: {
	productAttributes: ResponseProductAttribute[];
	physicalStoreId: string;
}): string[] => {
	const { marketId } = getStoreDataByPhysicalStoreId(physicalStoreId);

	const attributes =
		productAttributes
			?.map((attribute: ResponseProductAttribute) => {
				let validAttribute = true;

				if (
					attribute.identifier === 'PRO_MARKET_BUYABLE_EXCLUDE' ||
					attribute.identifier === MARKET_BUYABLE_EXCLUDE ||
					attribute.identifier === 'PRO_STORE_ONLY_AVAILABLE' ||
					attribute.identifier === STORE_ONLY_AVAILABLE
				) {
					validAttribute = validateStoreExclude(attribute, marketId);
				}

				if (
					attribute.identifier === 'PRO_STORE_BUYABLE_EXCLUDE' ||
					attribute.identifier === STORE_BUYABLE_EXCLUDE
				) {
					validAttribute = validateStoreExclude(attribute, physicalStoreId);
				}

				return validAttribute ? attribute.identifier : undefined;
			})
			.filter((attr) => typeof attr === 'string') ?? [];

	return FILTERED_PROMO_ICONS.filter((indentifier) => attributes.includes(indentifier));
};

export const isBuyableProduct = ({
	productAttributes,
	physicalStoreId,
}: {
	productAttributes: ResponseProductAttribute[];
	physicalStoreId: string;
}) => {
	const promotions = getProductPromotions({
		productAttributes,
		physicalStoreId,
	});
	return !PRODUCT_CARD_PROMO_ICONS_VALIDATE_STORE_EXCLUDE.B2C.some((attr) =>
		promotions.includes(attr)
	);
};
type ProductOfferPrice = {
	value: number;
};

const getProductOfferPrice = ({
	product,
}: {
	product: ProductType;
}): ProductOfferPrice | undefined => {
	const offerPriceObj = product.price?.find((p) => p.usage === PRODUCT_PRICE_USAGE.OFFER);

	if (!offerPriceObj) return undefined;

	const valueStr = offerPriceObj?.value;
	const value = valueStr ? parseFloat(valueStr) : 0;

	return {
		value,
	};
};

type ProductDisplayPrice = {
	value: number;
	field1: {
		LP: number;
	};
};

const getProductDisplayPrice = ({
	product,
}: {
	product: ProductType;
}): ProductDisplayPrice | undefined => {
	const displayPriceObj = product.price?.find((p) => p.usage === PRODUCT_PRICE_USAGE.DISPLAY);

	if (!displayPriceObj) return undefined;

	const valueStr = displayPriceObj?.value;
	const value = valueStr ? parseFloat(valueStr) : 0;

	const field1LPStr = displayPriceObj?.field1?.LP;
	const field1LP = field1LPStr ? parseFloat(field1LPStr) : 0;

	return {
		value,
		field1: {
			LP: field1LP,
		},
	};
};

export const getItemPartNumber = (product: ProductType): string => {
	if (product.type === 'product') {
		return product.items?.find((item) => item.type === 'item')?.partNumber ?? product.partNumber;
	}

	return product.partNumber;
};

export const getProductBrand = (product: ProductType): string => product.manufacturer;

export const getIsBundleFromSessionStorage = (partnumber: string): boolean => {
	try {
		const rawData = sessionStorage.getItem(IS_PARTNUMBER_FROM_BUNDLE_KEY);

		if (!rawData) return false;

		const parsedData = JSON.parse(rawData);

		if (parsedData === null || typeof parsedData !== 'object') return false;

		const bundlesMap: Map<string, string> = new Map(Object.entries(parsedData));

		return bundlesMap.has(partnumber);
	} catch (e) {
		return false;
	}
};

export const getDeliveryMethod = (orderItem: OrderItem) => {
	if (orderItem.shipModeCode === SHIP_MODE_CODE.PICK_UP_IN_STORE) {
		return PINTEREST_DELIVERY_CATEGORIES.PICK_UP_IN_STORE;
	}
	if (orderItem.shipModeCode === SHIP_MODE_CODE.MAIL) {
		return PINTEREST_DELIVERY_CATEGORIES.MAIL;
	}

	return undefined;
};

export const getFacebookDeliveryCategory = (deliveryCategory: string | undefined) => {
	if (deliveryCategory === PINTEREST_DELIVERY_CATEGORIES.PICK_UP_IN_STORE) {
		return FACEBOOK_DELIVERY_CATEGORIES.IN_STORE;
	}
	if (deliveryCategory === PINTEREST_DELIVERY_CATEGORIES.MAIL) {
		return FACEBOOK_DELIVERY_CATEGORIES.HOME_DELIVERY;
	}

	return undefined;
};
